import {
  AfterContentInit,
  AfterViewInit,
  Component,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  Renderer2,
  inject,
} from '@angular/core';

/**
 * Native-Angular ports of the AngularJS `ftCarouselCards` directive family
 * (account-v2/.../directives/carousel-cards/*). Drives the journey-cards
 * navigation strip at the top of /advocacy/journeys: cards register with
 * the parent FtCarouselCardsComponent, one is "active" (its `active`
 * attribute is present), and the parent maintains positional CSS classes
 * (`active`, `active-before-N`, `active-after-N`, `from-before`,
 * `from-after`) so the cards visually flow into / out of focus.
 *
 * Faithful port of the v1 controller's class-management logic. The v1
 * `adjustPosition()` step inserts an extra `<padding-element>` div on
 * either side of the cards row to balance the offset visually — that's
 * skipped here for the same reason FtExpandableContent skips its
 * `<secondary>` projection: it's a controller-side DOM-manipulation
 * the QA-login screenshot doesn't strictly require (the parent already
 * lays out via flex/grid CSS in `_journeys.less`). Add it via Renderer2
 * if a future gate reveals the offset gap.
 */
export interface CarouselCardEntry {
  /** The `ft-carousel-card="name"` attribute value. */
  name: string;
  /** The host element of the registered card directive (for class mutation). */
  el: HTMLElement;
  /** Mutable — flipped by activate(). */
  isActive: boolean;
}

@Component({
    selector: 'ft-carousel-cards',
    template: '<ng-content />',
    standalone: false
})
export class FtCarouselCardsComponent implements AfterContentInit {
  cards: CarouselCardEntry[] = [];
  activeCardIndex = 0;
  moveLeftCardName = '';
  moveRightCardName = '';

  /** v1's `options="{paddingElement: 'li'}"` — drives the off-balance
   *  padding elements that center the carousel around the active card. */
  @Input() options?: { paddingElement?: string };

  /** Mirrors v1's `on-after-init` binding shape — payload = the controller itself. */
  @Output() afterInit = new EventEmitter<{ controller: FtCarouselCardsComponent }>();

  private leftPaddingElem: HTMLElement | null = null;
  private rightPaddingElem: HTMLElement | null = null;

    private readonly renderer = inject(Renderer2);

  ngAfterContentInit(): void {
    // Children's addCard() calls have already fired during content init.
    // Adjust spots + position once more, then notify the parent template —
    // same shape as v1's link-fn afterInit callback.
    this.adjustSpots();
    this.adjustPosition();
    Promise.resolve().then(() => this.afterInit.emit({ controller: this }));
  }

  addCard(card: CarouselCardEntry): void {
    this.cards.push(card);
    if (card.isActive) this.activeCardIndex = this.cards.indexOf(card);
    this.adjustSpots();
    this.adjustMoveLeftAndRightCards();
    this.adjustPosition();
  }

  activate(card: CarouselCardEntry | string): void {
    const target: CarouselCardEntry | undefined =
      typeof card === 'string' ? this.cards.find((c) => c.name === card) : card;
    if (!target) return;
    this.cards.forEach((c, index) => {
      this.renderer.removeClass(c.el, 'from-before');
      this.renderer.removeClass(c.el, 'from-after');
      c.isActive = c === target;
      if (c.isActive && index < this.activeCardIndex) this.renderer.addClass(c.el, 'from-before');
      else if (c.isActive && index > this.activeCardIndex) this.renderer.addClass(c.el, 'from-after');
    });
    this.activeCardIndex = this.cards.indexOf(target);
    this.adjustMoveLeftAndRightCards();
    this.adjustSpots();
    this.adjustPosition();
  }

  /**
   * For a given card name, returns the SAME name if it's the active card,
   * otherwise the moveLeftCardName / moveRightCardName depending on which
   * side of the active card it sits on. Drives the section-trigger value
   * v1's template binds (clicking a non-active card moves toward it).
   */
  getMoveLeftOrRightCardName(cardName: string): string {
    const card = this.cards.find((c) => c.name === cardName);
    if (!card) return cardName;
    const idx = this.cards.indexOf(card);
    if (idx === this.activeCardIndex) return card.name;
    return idx > this.activeCardIndex ? this.moveRightCardName : this.moveLeftCardName;
  }

  moveLeft(): void {
    if (this.activeCardIndex > 0) this.activate(this.cards[this.activeCardIndex - 1]);
  }

  moveRight(): void {
    if (this.activeCardIndex < this.cards.length - 1) this.activate(this.cards[this.activeCardIndex + 1]);
  }

  private adjustMoveLeftAndRightCards(): void {
    if (!this.cards.length) return;
    this.moveLeftCardName = this.activeCardIndex > 0 ? this.cards[this.activeCardIndex - 1].name : this.cards[0].name;
    this.moveRightCardName =
      this.activeCardIndex < this.cards.length - 1
        ? this.cards[this.activeCardIndex + 1].name
        : this.cards[this.cards.length - 1].name;
  }

  /** Faithful port of v1's adjustPosition — creates left/right padding
   *  elements (tag name from `options.paddingElement`, e.g. 'li') and
   *  inserts them around the cards row to balance the active-card offset.
   *  Without these, the carousel sticks to the left edge of its parent. */
  private adjustPosition(): void {
    if (!this.options?.paddingElement) return;
    if (!this.cards.length) return;
    const parent = this.cards[0].el.parentNode as HTMLElement | null;
    if (!parent) return;

    // Strip any prior padding elements before re-inserting (re-runs on
    // every addCard / activate).
    if (this.leftPaddingElem?.parentNode) this.leftPaddingElem.parentNode.removeChild(this.leftPaddingElem);
    if (this.rightPaddingElem?.parentNode) this.rightPaddingElem.parentNode.removeChild(this.rightPaddingElem);

    this.leftPaddingElem = this.renderer.createElement(this.options.paddingElement) as HTMLElement;
    this.rightPaddingElem = this.renderer.createElement(this.options.paddingElement) as HTMLElement;
    this.renderer.insertBefore(parent, this.leftPaddingElem, this.cards[0].el);
    this.renderer.appendChild(parent, this.rightPaddingElem);

    const activeIndex = this.activeCardIndex;
    const elemsLeft = activeIndex;
    const elemsRight = this.cards.length - 1 - activeIndex;
    const diff = Math.abs(elemsLeft - elemsRight);
    if (elemsRight > elemsLeft) {
      this.renderer.addClass(this.leftPaddingElem, `active-before-${Math.abs(elemsLeft + 1)}`);
      this.renderer.addClass(this.leftPaddingElem, `offset-${diff}`);
      this.renderer.addClass(this.rightPaddingElem, 'offset-0');
    } else if (elemsRight < elemsLeft) {
      this.renderer.addClass(this.leftPaddingElem, 'offset-0');
      this.renderer.addClass(this.rightPaddingElem, `active-after-${Math.abs(elemsRight + 1)}`);
      this.renderer.addClass(this.rightPaddingElem, `offset-${diff}`);
    }
  }

  /** Same shape as v1's adjustSpots — strip stale active-{before,after}-N
   *  classes from every card, then apply the right one based on distance
   *  from the active index. */
  private adjustSpots(): void {
    if (!this.cards.length) return;
    const activeIndex = this.cards.findIndex((c) => c.isActive);
    if (activeIndex < 0) return;
    this.activeCardIndex = activeIndex;
    for (const c of this.cards) {
      this.renderer.removeClass(c.el, 'active');
      for (let i = 0; i < this.cards.length; i++) {
        this.renderer.removeClass(c.el, `active-before-${i}`);
        this.renderer.removeClass(c.el, `active-after-${i}`);
      }
    }
    this.cards.forEach((c, index) => {
      if (c.isActive) this.renderer.addClass(c.el, 'active');
      else if (index < activeIndex) this.renderer.addClass(c.el, `active-before-${activeIndex - index}`);
      else if (index > activeIndex) this.renderer.addClass(c.el, `active-after-${index - activeIndex}`);
    });
  }
}

/**
 * `<li ft-carousel-card="prestay" active>` — attribute directive. Reads
 * its name input + the presence-only `active` attribute, then registers
 * itself with the parent FtCarouselCardsComponent in ngOnInit. v1's
 * `require: ["^ftCarouselCards", "ftCarouselCard"]` translates to
 * `@Host()` injection of the parent component.
 */
@Directive({
    selector: '[ft-carousel-card]',
    standalone: false
})
export class FtCarouselCardDirective implements AfterViewInit {
  @Input('ft-carousel-card') name!: string;
  /** Presence-only attribute mirroring v1's `attrs.hasOwnProperty("active")`. */
  @Input() active: boolean | '' | null | undefined;

    private readonly parent = inject(FtCarouselCardsComponent, { host: true });
    private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);

  ngAfterViewInit(): void {
    this.parent?.addCard({
      name: this.name,
      el: this.el.nativeElement,
      // `active` from attribute presence: '' or true or non-null → truthy.
      isActive: this.active === '' || this.active === true || (this.active != null && String(this.active) !== 'false'),
    });
  }
}

/**
 * `<a ft-carousel-cards-move-left>` — attribute directive that, on click,
 * tells the host carousel to move the active card left. Same shape for
 * move-right.
 */
@Directive({
    selector: '[ft-carousel-cards-move-left]',
    standalone: false
})
export class FtCarouselCardsMoveLeftDirective {
    private readonly parent = inject(FtCarouselCardsComponent, { host: true, optional: true });
  @HostListener('click') onClick(): void { this.parent?.moveLeft(); }
}

@Directive({
    selector: '[ft-carousel-cards-move-right]',
    standalone: false
})
export class FtCarouselCardsMoveRightDirective {
    private readonly parent = inject(FtCarouselCardsComponent, { host: true, optional: true });
  @HostListener('click') onClick(): void { this.parent?.moveRight(); }
}

/**
 * `<li ft-carousel-card-move-left-or-right>` — clicking a non-active card
 * moves the carousel toward it (uses the host card's name to decide
 * direction via the parent's getMoveLeftOrRightCardName).
 */
@Directive({
    selector: '[ft-carousel-card-move-left-or-right]',
    standalone: false
})
export class FtCarouselCardMoveLeftOrRightDirective {
    private readonly parent = inject(FtCarouselCardsComponent, { host: true, optional: true });
    private readonly card = inject(FtCarouselCardDirective, { host: true, optional: true });

  @HostListener('click') onClick(): void {
    if (!this.parent || !this.card?.name) return;
    const target = this.parent.getMoveLeftOrRightCardName(this.card.name);
    this.parent.activate(target);
  }
}

/**
 * `<a ft-carousel-card-trigger="prestay">` — clicking activates the named
 * card directly. Same shape as ftSectionTrigger from the sections family.
 */
@Directive({
    selector: '[ft-carousel-card-trigger]',
    standalone: false
})
export class FtCarouselCardTriggerDirective {
  @Input('ft-carousel-card-trigger') cardName!: string;
    private readonly parent = inject(FtCarouselCardsComponent, { optional: true });
  @HostListener('click') onClick(): void { this.parent?.activate(this.cardName); }
}
